// Message Handlers - Handles message creation, sending, and chat logic
import axios from 'axios';
import shortUUID from 'short-uuid';
import { processStream } from '../../../utils/streamingProcessor';
import { getCurrentModeConfig } from './modeConfigs';

/**
 * Create a new message object
 */
export const createMessage = ({ content, threadId, direction }) => {
  const newMessage = {
    id: shortUUID().new(),
    threadId: threadId,
    content: content,
    direction: direction || 'sent',
    timestamp: new Date().getTime(),
  };
  return newMessage;
};

/**
 * Reset messages by removing specific message IDs
 */
export const resetMessages = (setMessages, messageIds) => {
  setMessages((prevMessages) => prevMessages.filter(message => !messageIds.includes(message.id)));
};

/**
 * Create a new thread
 */
export const createThread = async (firstMessage, newThreadId, user, workspace, chatMode, currentForecast = null) => {
  const words = firstMessage.trim().split(' ').slice(0, 5).join(' ');
  const threadName = words.length > 0 ? words : 'New Thread';

  const newThread = {
    id: newThreadId,
    title: threadName,
    userId: user.id,
    mode: chatMode, // Include the current chat mode
  };
  
  try {
    // Always create threads via /api/threads; include forecastId if present
    const activeForecastId = currentForecast?.id || null;
    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/threads/create-thread`, {
      id: newThread.id,
      userId: newThread.userId,
      title: newThread.title,
      mode: newThread.mode, // Send the mode to backend
      workspaceId: workspace.id,
      vectorStoreId: workspace.vectorStoreId,
      forecastId: activeForecastId || null
    }, { withCredentials: true });

    return newThread;

  } catch (error) {
    console.error('Error creating thread:', error);
    return null;
  }
};

/**
 * Update thread name
 */
export const updateThreadName = (threadId, newName, mode) => {
  const event = new CustomEvent('updateThreadName', {
    detail: { threadId, newName, mode },
  });
  window.dispatchEvent(event);
};

/**
 * Ensure forecast and thread exist for dashboard mode
 */
export const ensureForecastAndThread = async ({
  initialMessage,
  user,
  workspace,
  forecast,
  setForecast,
  componentsFetched,
  loadForecastComponents,
  thread,
  setThread,
  updateThreadName
}) => {
  // Ensure a forecast of type 'dashboard' exists (create if missing), then ensure a forecast thread exists
  let forecastId = forecast?.id;
  if (!forecastId) {
    try {
      const createRes = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/create-forecast`,
        {
          creatorUserId: user.id,
          title: forecast?.title || 'Untitled Dashboard',
          accessType: 'team-wide',
          workspaceId: workspace.id,
          type: 'dashboard'
        },
        { withCredentials: true }
      );
      if (createRes.data && createRes.data.id) {
        forecastId = createRes.data.id;
        setForecast(createRes.data);
      }
    } catch (err) {
      console.error('Error creating forecast (dashboard type):', err);
      throw err;
    }
  } else if (!componentsFetched) {
    await loadForecastComponents(forecastId, user, workspace);
  }

  // Ensure forecast-level thread exists
  if (!thread || !thread.id) {
    const newThreadId = shortUUID().new();
    try {
      const createThreadRes = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/create-thread`,
        {
          id: newThreadId,
          title: (initialMessage || '').substring(0, 50) || 'New Thread',
          workspaceId: workspace.id,
          userId: user.id,
          forecastId: forecastId
        },
        { withCredentials: true }
      );
      if (createThreadRes.data && createThreadRes.data.id) {
        setThread(createThreadRes.data);
        updateThreadName(createThreadRes.data.id, createThreadRes.data.title, 'dashboard');
      }
    } catch (err) {
      console.error('Error creating forecast thread:', err);
      throw err;
    }
  }

  return { forecastId, threadId: thread?.id || null };
};

/**
 * Chat with assistant - handles both general and forecast/dashboard modes
 */
/**
 * Upload a file to the temp endpoint so chatWithAI can process it.
 * Returns { tempPath, fileName, fileType } on success, or null on failure.
 */
const uploadFileForChat = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/upload-temp`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
    );
    return res.data || null;
  } catch (err) {
    console.error('Error uploading file for chat:', err);
    return null;
  }
};

export const chatWithAssistant = async ({
  message,
  threadId,
  researchModeOverride,
  attachedFile,
  chatMode,
  user,
  workspace,
  thread,
  forecast,
  uploadedFile,
  setMessages,
  setThinking,
  setStartedTyping,
  setShowProcessingMessage,
  setProcessingMessage,
  setStreamingActionData,
  setShowUsagePopup,
  setError,
  setForecast,
  setComponents,
  setThread,
  setCurrentForecast,
  setForecasts,
  setScenarios,
  setActiveScenario,
  ensureForecastAndThread,
  // Forecast-related setters
  setIsBuildingForecast,
  setLayoutView,
  setComponentsFetched,
  loadForecastComponents,
  onChecklistUpdate
}) => {
  const modeConfig = getCurrentModeConfig(chatMode);

  // Create an initial empty message to show in the UI
  const result = createMessage({ content: JSON.stringify([]), threadId, direction: 'received' });
  setMessages((prevMessages) => [...prevMessages, result]);
  
  setThinking(true);
  setStartedTyping(false);
  setShowProcessingMessage(false);
  setProcessingMessage('');
  setStreamingActionData([]);

  // If a file is attached, upload it to temp storage first
  let contextFile = null;
  if (attachedFile) {
    contextFile = await uploadFileForChat(attachedFile);
  }

  try {
    await processStream({
      apiUrl: `${process.env.REACT_APP_API_BASE_URL}/api/threads/chat`,
      payload: {
        threadId: threadId,
        message: message,
        workspaceId: workspace.id,
        userId: user.id,
        resultMessageId: result.id,
        attachmentName: uploadedFile?.name || thread?.attachmentName || null,
        taskPanelEnabled: true,
        mode: modeConfig.key,
        forecastId: forecast?.id || null,
        contextFile: contextFile || null
      },
      initialMessage: result,
      updateMessages: setMessages,
      setShowProcessingMessage,
      setProcessingMessage,
      setStreamingActionData,
      setStartedTyping,
      resetMessages: ({ messageIds }) => resetMessages(setMessages, messageIds),
      callbacks: {
        onUsageLimit: () => setShowUsagePopup(true),
        onError: (error) => {
          console.error('Streaming error:', error);
          setError("Hmm.. something went wrong. Please try again later.");
        },
        onChecklistUpdate: (checklist) => {
          if (onChecklistUpdate) {
            onChecklistUpdate(checklist);
          } else if (setForecast) {
            setForecast(prev => prev ? { ...prev, checklist } : prev);
          }
        },
        onThreadMetadata: (threadData) => {
          // Backend auto-created a thread - update local thread state
          console.log('Received thread metadata from backend:', threadData);
          setThread({
            id: threadData.id,
            title: threadData.title,
            mode: threadData.mode,
            workspaceId: workspace.id,
            userId: user.id
          });
          // Update currentForecast + forecasts array so the threadId persists
          // across scenario switches and page refreshes
          if (threadData.forecastId && setCurrentForecast) {
            setCurrentForecast(prev => prev && prev.id === threadData.forecastId
              ? { ...prev, threadId: threadData.id }
              : prev
            );
            if (setForecasts) {
              setForecasts(prev => prev.map(f =>
                f.id === threadData.forecastId ? { ...f, threadId: threadData.id } : f
              ));
            }
          }
        },
        onForecastProgress: async (progressData) => {
          // Handle forecast progress updates
          console.log('Forecast progress:', progressData);
          
          // Handle calendar definition (from forecast_define_calendar tool)
          if (progressData.subtype === 'forecast_calendar_defined' && progressData.calendar && setForecast) {
            console.log('Calendar defined, updating forecast:', progressData.calendar);
            setForecast(prev => ({
              ...prev,
              id: prev?.id || progressData.forecastId,
              calendar: progressData.calendar
            }));
            
            // Reload components to reflect new calendar structure
            if (loadForecastComponents && progressData.forecastId) {
              try {
                if (setComponentsFetched) setComponentsFetched(false);
                await loadForecastComponents(progressData.forecastId, user, workspace);
                if (setComponentsFetched) setComponentsFetched(true);
              } catch (err) {
                console.error('Error reloading components after calendar definition:', err);
              }
            }
          }
          
          // Update forecast ID if needed
          if (progressData.forecastId && setForecast) {
            if (progressData.step === 0 && !forecast?.id) {
              // First progress event - initialize forecast
              console.log('First progress event - initializing forecast:', progressData.forecastId);
              setForecast({
                id: progressData.forecastId,
                status: 'in-progress'
              });
            } else {
              // Update forecast ID
              setForecast(prev => ({
                ...prev,
                id: progressData.forecastId
              }));
            }
          }
          
          // Handle real-time forecast updates (row added/updated/deleted, query added, step completed)
          if (progressData.action && progressData.forecastId) {
            // Handle step completion - update build_steps and status immediately
            if (progressData.action === 'step_completed' && setForecast && progressData.build_steps) {
              console.log('Step completed, updating build_steps:', progressData.build_steps);
              
              setForecast(prev => {
                const updates = {
                  ...prev,
                  id: prev?.id || progressData.forecastId,
                  build_steps: progressData.build_steps
                };
                
                // Update status if provided (will be 'completed' when all steps are done)
                if (progressData.status) {
                  updates.status = progressData.status;
                  console.log('Forecast status updated to:', progressData.status);
                }
                
                return updates;
              });
              
              // If forecast is now complete, trigger final component reload and canvas view switch
              if (progressData.status === 'completed') {
                console.log('All build steps completed, finalizing forecast');
                
                // Reload components one final time
                if (loadForecastComponents && progressData.forecastId) {
                  try {
                    if (setComponentsFetched) setComponentsFetched(false);
                    await loadForecastComponents(progressData.forecastId, user, workspace);
                    if (setComponentsFetched) setComponentsFetched(true);
                  } catch (err) {
                    console.error('Error loading final forecast components:', err);
                  }
                }
                
                // Switch to canvas view
                if (setLayoutView) setLayoutView('canvas');
                if (setIsBuildingForecast) setIsBuildingForecast(false);
              }
            }

            // Handle scenario lifecycle events
            if (progressData.action === 'scenario_created' && progressData.scenarioName && setScenarios) {
              setScenarios(prev => {
                const exists = prev.some(s => s.scenarioName === progressData.scenarioName);
                if (exists) return prev;
                return [...prev, { scenarioName: progressData.scenarioName }];
              });
            }

            if (progressData.action === 'scenario_deleted' && progressData.scenarioName) {
              if (setScenarios) {
                setScenarios(prev => prev.filter(s => s.scenarioName !== progressData.scenarioName));
              }
              if (setActiveScenario) {
                setActiveScenario(prev => prev === progressData.scenarioName ? null : prev);
              }
            }

            // Reload components only for actions that change forecast data
            const componentActions = ['row_added', 'row_updated', 'row_deleted', 'query_added', 'scenario_updated'];
            if (componentActions.includes(progressData.action)) {
              console.log(`Forecast ${progressData.action}, reloading components...`);
              
              // Reload components for live update
              if (loadForecastComponents && forecast?.id) {
                try {
                  // Reset fetched flag to force reload
                  if (setComponentsFetched) setComponentsFetched(false);
                  await loadForecastComponents(progressData.forecastId, user, workspace);
                  if (setComponentsFetched) setComponentsFetched(true);
                } catch (err) {
                  console.error('Error reloading forecast components:', err);
                }
              }
            }
          }
          
          // Handle completion - check both progressType and forecast.status (including from step_completed action)
          const isComplete = progressData.progressType === 'complete' || 
                           (progressData.forecast && progressData.forecast.status === 'completed') ||
                           (progressData.action === 'step_completed' && progressData.status === 'completed');
          
          if (isComplete && progressData.forecast) {
            console.log('Forecast completed, loading components and switching to canvas view');
            setForecast(progressData.forecast);
            
            // Load components
            if (loadForecastComponents && progressData.forecast.id) {
              try {
                await loadForecastComponents(progressData.forecast.id, user, workspace  );
                if (setComponentsFetched) setComponentsFetched(true);
              } catch (err) {
                console.error('Error loading forecast components:', err);
              }
            }
            
            // Switch to canvas view
            if (setLayoutView) setLayoutView('canvas');
            
            // Reset building flag
            if (setIsBuildingForecast) setIsBuildingForecast(false);
          }
        }
      }
    });
  } catch (error) {
    console.error('Error saving assistant message:', error);
  } finally {
    setShowProcessingMessage(false);
    setProcessingMessage('');
    setStreamingActionData([]);
    setThinking(false);
  }
};

/**
 * Handle sending a message
 * NOTE: Thread management is now fully handled by the backend
 * - Backend fetches thread from workspace (doesn't trust frontend threadId)
 * - Backend auto-creates thread if none exists for the workspace
 * - Frontend receives thread metadata via stream
 */
export const handleSendMessage = async ({
  inputValue,
  isUploadingFile,
  setInputValue,
  setThinking,
  setShowGreeting,
  uploadedFile,
  setUploadedFile,
  setIsUploadingFile,
  setShowProcessingMessage,
  setProcessingMessage,
  setStreamingActionData,
  setStartedTyping,
  thread,
  setMessages,
  createThread,
  setThread,
  updateThreadName,
  chatWithAssistant,
  updateSetupProgress,
  setError
}) => {
  if (!inputValue.trim() || isUploadingFile) return;
  const userMessageContent = inputValue.trim();
  setInputValue('');
  setThinking(true);
  setShowGreeting(false);

  if (uploadedFile) {
    setUploadedFile(null);
    setIsUploadingFile(false);
  }
  
  // Reset processing states to ensure feedback doesn't show prematurely
  setShowProcessingMessage(false);
  setProcessingMessage('');
  setStreamingActionData([]);
  setStartedTyping(false);

  try {
    // Create message with current thread ID (or null if no thread exists)
    const msg = createMessage({ 
      content: userMessageContent, 
      threadId: thread?.id || null, 
      direction: 'sent' 
    });
    setMessages((prevMessages) => [...prevMessages, msg]);

    // Send to chat endpoint - backend will auto-create thread if needed
    await chatWithAssistant({ message: msg, threadId: thread?.id || null });
    
    // If no thread existed, we need to fetch it after the chat completes
    // The backend will have created it, but we need to update our local state
    // This will be handled by a subsequent re-fetch or the response metadata
    
    updateSetupProgress();
  } catch (error) {
    setError("Hmm.. something went wrong. Please try again later.");
  } finally {
    setStartedTyping(false);
  }
};

/**
 * Message handlers factory - creates bound functions with all dependencies
 */
export const createMessageHandlers = ({
  user,
  workspace,
  chatMode,
  thread,
  forecast,
  uploadedFile,
  componentsFetched,
  loadForecastComponents,
  updateURL,
  updateThreadName: updateThreadNameFn,
  updateSetupProgress,
  // Setters
  setMessages,
  setThinking,
  setStartedTyping,
  setShowProcessingMessage,
  setProcessingMessage,
  setStreamingActionData,
  setShowUsagePopup,
  setError,
  setForecast,
  setComponents,
  setThread,
  setInputValue,
  setShowGreeting,
  setUploadedFile,
  setIsUploadingFile,
  // Forecast-related setters (optional for Threads.jsx integration)
  setIsBuildingForecast,
  setLayoutView,
  setComponentsFetched,
  setCurrentForecast,
  setForecasts,
  setScenarios,
  setActiveScenario,
  onChecklistUpdate
}) => {
  const boundCreateThread = (firstMessage, newThreadId, currentForecastOverride = null) =>
    createThread(firstMessage, newThreadId, user, workspace, chatMode, currentForecastOverride || forecast);
  
  const boundEnsureForecastAndThread = (params) => ensureForecastAndThread({
    ...params,
    user,
    workspace,
    forecast,
    setForecast,
    componentsFetched,
    loadForecastComponents,
    thread,
    setThread,
    updateURL,
    updateThreadName: updateThreadNameFn
  });

  const boundChatWithAssistant = (params) => chatWithAssistant({
    ...params,
    chatMode,
    user,
    workspace,
    thread,
    forecast,
    uploadedFile,
    setMessages,
    setThinking,
    setStartedTyping,
    setShowProcessingMessage,
    setProcessingMessage,
    setStreamingActionData,
    setShowUsagePopup,
    setError,
    setForecast,
    setComponents,
    setThread,
    setCurrentForecast,
    setForecasts,
    setScenarios,
    setActiveScenario,
    ensureForecastAndThread: boundEnsureForecastAndThread,
    setIsBuildingForecast,
    setLayoutView,
    setComponentsFetched,
    loadForecastComponents,
    onChecklistUpdate
  });

  const boundHandleSendMessage = (inputValue, isUploadingFile) => handleSendMessage({
    inputValue,
    isUploadingFile,
    setInputValue,
    setThinking,
    setShowGreeting,
    uploadedFile,
    setUploadedFile,
    setIsUploadingFile,
    setShowProcessingMessage,
    setProcessingMessage,
    setStreamingActionData,
    setStartedTyping,
    thread,
    setMessages,
    createThread: boundCreateThread,
    updateURL,
    setThread,
    updateThreadName: updateThreadNameFn,
    chatWithAssistant: boundChatWithAssistant,
    updateSetupProgress,
    setError
  });

  return {
    createMessage,
    resetMessages: ({ messageIds }) => resetMessages(setMessages, messageIds),
    createThread: boundCreateThread,
    updateThreadName: updateThreadNameFn,
    ensureForecastAndThread: boundEnsureForecastAndThread,
    chatWithAssistant: boundChatWithAssistant,
    handleSendMessage: boundHandleSendMessage
  };
};
